import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  CalendarDays, CheckSquare, ChevronRight,
  Mail, Mic, PhoneCall,
  Send, Speaker, Square, UserRound, Paperclip, Globe,
} from "lucide-react";
import {
  createApprovalToken, createMemory, createMonitor, createReminder, createTask,
  draftEvent, draftReply, publishEvent, updateCalendarEvent, updateMonitor, updateReminder, updateTask,
  getNextAction, runAgent, getVoiceStatus, synthesizeVoice, transcribeVoice,
  createCollaborationDraft, sendCollaboration, respondCollaboration,
  getEmailTaskSuggestions,acceptEmailTaskSuggestion,getWorkContext,setEmailAttention,
} from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { safeExternalUrl } from "@/lib/safeUrl";
import { AcaiLogo, AcaiMark } from "@/components/AcaiLogo";
import { VoiceOrb } from "@/components/VoiceOrb";
import { voiceDecision } from "@/lib/voiceDecision";
import { userError } from "@/lib/userError";
import { useAssistantViewport } from "@/lib/useAssistantViewport";
import { ActionPreview, previewLines, previewReady } from "@/components/ActionPreview";
import { validateAssistantFiles } from "@/lib/assistantFiles";
import { actionResources, notifyDataChanged } from "@/lib/dataChanges";
import {emailTaskAction} from "@/lib/emailTaskAction";
import {EmailTaskReviewNotice} from "@/components/EmailTaskReviewNotice";
import { playSpeech, speechAbortError } from "@/lib/speechPlayback";
import {solutionActionAllowed} from "@/lib/workspaceSolution";
import {solutionModelText} from "@/lib/solutionConversation";
import {ConversationSolution} from "./ConversationSolution";
import {EmailAttentionButton,attentionMessage} from "./EmailAttentionButton";
import {SolutionSummary} from "./SolutionSummary";

const QUICK_PROMPTS = [
  { label: <>Anna aamun<br />yhteenveto</>, prompt: "Anna aamuyhteenveto sähköposteista, kalenterista, tehtävistä, muistutuksista ja minulle merkittävistä uutisista", icon: CalendarDays, tone: "cyan" },
  { label: <>Yhteenveto<br />sähköposteista</>, prompt: "Yhteenveto sähköposteista", icon: Mail, tone: "violet" },
  { label: <>Mitä teen<br />nyt?</>, prompt: "Mitä minun kannattaa tehdä nyt?", icon: CheckSquare, tone: "cyan" },
  { label: <>Pyydä luvallinen<br />puhelu</>, prompt: "Pyydä luvallinen puhelu", icon: PhoneCall, tone: "violet" },
];

const PENDING_ACTION_TTL_MS = 2 * 60 * 1000;
const VAD_END_SILENCE_MS = 1700;
const VAD_MIN_SPEECH_MS = 220;
const VAD_NO_SPEECH_RESTART_MS = 20000;

const agentErrorMessage = (error) => {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || "").toLocaleLowerCase("fi-FI");
  const withRequestId = (message) => message;
  if (status === 402 || status === 429 || /credit|quota|käyttöraja|kiintiö/.test(detail)) {
    return withRequestId("Assistenttipalvelun käyttöraja on nyt täynnä. Muut sovelluksen toiminnot ovat edelleen käytettävissä.");
  }
  if (status === 503 && /yhdistetty|määritetty|configured/.test(detail)) {
    return withRequestId("Assistenttiyhteys ei ole vielä käytettävissä. Tarkista yhteys asetuksista.");
  }
  if (status === 504 || error?.code === "ECONNABORTED") {
    return withRequestId("Taustapalvelu ei vastannut ajoissa. Odota hetki ja yritä uudelleen.");
  }
  return userError(error, "Vastausta ei saatu. Voit muokata viestiä ja yrittää uudelleen.");
};

const isNextActionRequest = (text) => {
  const normalized = text.toLocaleLowerCase("fi-FI").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return (
    /mita\s+(?:(?:minun|mun)\s+)?(?:pitaisi\s+|kannattaisi\s+|kannattaa\s+)?tehda\s+(?:nyt|seuraavaksi)/.test(normalized) ||
    /mihin\s+(?:(?:minun|mun)\s+)?(?:kannattaisi\s+|kannattaa\s+)?keskittya/.test(normalized) ||
    /mika\s+on\s+(?:minun\s+)?seuraava\s+(?:tehtava|askel)/.test(normalized) ||
    /^(?:mita|mitas)\s+(?:sitten|seuraavaksi)[.!?]*$/.test(normalized.trim()) ||
    /^mita\s+nyt[.!?]*$/.test(normalized.trim())
  );
};

const actionTitle = (action) => (
  action.type === "collaboration_response" ? `Vastaus: ${action.preview?.title || "Yhteistyöpyyntö"}` :
  action.title || action.new_title || action.content ||
  (action.type === "email_draft" ? "Sähköpostivastausluonnos" : "Päivitä valittu tieto")
);

const actionTypeLabel = (action) => ({
  calendar_event: "Kalenteri",
  calendar_event_update: "Kalenterin muutos",
  reminder: "Muistutus",
  reminder_update: "Muistutuksen muutos",
  task: action.visibility === "company" ? "Yritystehtävä" : "Tehtävä",
  task_update: "Tehtävän muutos",
  email_task_accept: "Sähköpostista oma tehtävä",
  email_draft: "Luonnos, ei lähetystä",
  collaboration_request: "Sisäinen yhteistyöpyyntö",
  collaboration_response: "Vastaus yhteistyöpyyntöön",
  memory: "Muisti",
  monitor: "Seuranta",
  monitor_update: "Seurannan muutos",
}[action.type] || "Toiminta");

const VoiceMessage = ({ message }) => (
  <div className={cn("voice-message-card", message.role === "assistant" ? "is-assistant" : "is-user")}>
    <div className="voice-message-avatar">{message.role === "assistant" ? <AcaiMark /> : <UserRound />}</div>
    <div className="voice-message-copy">
      <div className="voice-message-meta"><span>{message.role === "assistant" ? "ACAI" : "Sinä"}</span><time>{message.time || "nyt"}</time></div>
      {message.role==="assistant"&&message.solution_summary?<SolutionSummary summary={message.solution_summary} response={message.text}/>:<p>{message.text}</p>}
      {message.role==="assistant"&&<EmailTaskReviewNotice review={message.emailTaskReview}/>}
      {message.memoryFailed && <p className="mt-2 text-xs text-muted-foreground" role="status">Muistin päivitys ei onnistunut. Tarkista tiedot asetuksista.</p>}
      {message.memoryCount > 0 && <p className="mt-2 text-xs text-muted-foreground" role="status">Muisti päivitetty: {message.memoryCount} {message.memoryCount === 1 ? "tieto" : "tietoa"}. Voit muokata tai poistaa tiedot asetusten muistista.</p>}
      {message.role === "assistant" && message.sources?.length > 0 && (
        <div className="voice-news-sources" aria-label="Vastauksen lähteet">
          <span>Lähteet</span>
          {message.sources.slice(0, 6).map((source) => ({ ...source, safeUrl: safeExternalUrl(source.url) })).filter((source) => source.safeUrl).map((source) => (
            <a
              key={`${source.url}-${source.title}`}
              href={source.safeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {source.source || source.title}
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const VoiceSheet = ({ open, onOpenChange, initialPrompt = "", collaborationRequestId = null, emailTaskSuggestionId = null, workContext = null, autoStartVoice = false, initialSolution = null }) => {
  const autoVoiceStarted=useRef(false);
  const solutionAdopted=useRef(false);
  const scoped=Boolean(collaborationRequestId||emailTaskSuggestionId||workContext);
  const [selectedSource,setSelectedSource]=useState(null);
  const [acknowledgedAttention,setAcknowledgedAttention]=useState("");
  const [sourceError,setSourceError]=useState(false);
  const [sourceReload,setSourceReload]=useState(0);
  useEffect(()=>{
    let active=true;
    setSelectedSource(null);setSourceError(false);
    if(open&&workContext)getWorkContext(workContext).then(value=>{if(active)setSelectedSource(value);}).catch(()=>{if(active)setSourceError(true);});
    return()=>{active=false;};
  },[open,workContext,sourceReload]);
  const assistantViewport = useAssistantViewport(open);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [messages, setMessages] = useState([]);
  const [draftMessage, setDraftMessage] = useState("");
  const [knowledgeMode, setKnowledgeMode] = useState(false);
  const [allowWeb, setAllowWeb] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentConsent, setAttachmentConsent] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const attachmentInputRef = useRef(null);
  const knowledgeEndRef = useRef(null);
  useEffect(() => {
    if (knowledgeMode && messages.length) knowledgeEndRef.current?.scrollIntoView?.({block:"nearest"});
  }, [knowledgeMode, messages.length]);
  const [failedRequest, setFailedRequest] = useState("");
  const [emailTaskReload,setEmailTaskReload]=useState(0);
  const [visibleMessageCount, setVisibleMessageCount] = useState(8);
  const [thinking, setThinking] = useState(false);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [supportsSpeech, setSupportsSpeech] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [captureStatus, setCaptureStatus] = useState("idle");
  const [speaking, setSpeaking] = useState(false);
  const [playbackActive, setPlaybackActive] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");
  const [voiceIssue, setVoiceIssue] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [solutionRevision,setSolutionRevision]=useState(0);
  const [solutionPlanning,setSolutionPlanning]=useState(null);
  const [proposalExpired, setProposalExpired] = useState(false);
  const [proposalKind, setProposalKind] = useState("");
  const [addingActions, setAddingActions] = useState(false);
  const [actionError, setActionError] = useState("");
  const [conversationActive, setConversationActive] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const recognitionErrorRef = useRef(false);
  const conversationActiveRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const utteranceRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const levelFrameRef = useRef(null);
  const levelSourceRef = useRef(null);
  const analyserRef = useRef(null);
  const vadNoSpeechRef = useRef(false);
  const discardRecordingRef = useRef(false);
  const speechRunRef = useRef(0);
  const speechAbortRef = useRef(null);
  const playbackCancelRef = useRef(null);
  const sessionRunRef = useRef(0);
  const speakerOnRef = useRef(true);
  const automaticPromptRef = useRef("");
  const pendingCreatedAtRef = useRef(0);
  const actionSubmissionRef = useRef(false);
  const composerRef = useRef(null);
  const requestBusyRef = useRef(false);
  const proposalRef = useRef(null);
  const proposalSignature = JSON.stringify(pendingActions);
  proposalRef.current = { signature: proposalSignature, actions: pendingActions };
  const shownProposal = useRef(null);
  useEffect(()=>{
    if(!open||!workContext||!selectedSource||!initialSolution||solutionAdopted.current)return;
    solutionAdopted.current=true;
    const action=initialSolution.action;
    const valid=action&&solutionActionAllowed(action,workContext,selectedSource)&&Number.isFinite(initialSolution.createdAt)&&Date.now()-initialSolution.createdAt<=PENDING_ACTION_TTL_MS&&initialSolution.createdAt<=Date.now();
    setMessages([{role:"assistant",time:"nyt",text:initialSolution.response,solution_summary:initialSolution.solution_summary,modelText:solutionModelText(initialSolution.response,valid?[action]:[])}]);
    if(valid){
      setPendingActions([{...action,client_action_id:globalThis.crypto?.randomUUID?.()||`solution-${Date.now()}`}]);pendingCreatedAtRef.current=initialSolution.createdAt;
      setSolutionRevision(1);
      setSolutionPlanning(initialSolution.planning||null);
    }
  },[open,workContext,selectedSource,initialSolution]);
  useEffect(() => { shownProposal.current = pendingActions.length ? proposalSignature : null; }, [proposalSignature, pendingActions.length]);

  useEffect(() => {
    if (!pendingActions.length || !pendingCreatedAtRef.current) {
      setProposalExpired(false);
      return undefined;
    }
    const remaining = Math.max(0, PENDING_ACTION_TTL_MS - (Date.now() - pendingCreatedAtRef.current));
    const timer = window.setTimeout(() => setProposalExpired(true), remaining);
    return () => window.clearTimeout(timer);
  }, [pendingActions]);

  const stopLevelMeter = () => {
    if (levelFrameRef.current) cancelAnimationFrame(levelFrameRef.current);
    levelFrameRef.current = null;
    try { levelSourceRef.current?.disconnect(); } catch { /* already disconnected */ }
    levelSourceRef.current = null;
    analyserRef.current = null;
    setVoiceLevel(0);
  };

  const startLevelMeter = (analyser, { detectSpeech = false } = {}) => {
    stopLevelMeter();
    analyserRef.current = analyser;
    const samples = new Uint8Array(analyser.fftSize);
    let noiseFloor = 0.012;
    let speechCandidateAt = 0;
    let speechStarted = false;
    let silenceStartedAt = 0;
    let lastPaintAt = 0;
    const startedAt = performance.now();

    const measure = (now) => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) {
        const normalized = (sample - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / samples.length);
      const visualLevel = Math.min(1, Math.max(0, (rms - 0.008) * 9));
      if (now - lastPaintAt > 45) {
        setVoiceLevel(visualLevel);
        lastPaintAt = now;
      }

      if (detectSpeech && mediaRecorderRef.current?.state === "recording") {
        // Learn the room level only from quiet samples. Previously the second
        // turn could teach the detector that the user's voice itself was room
        // noise, which meant the recording never reached the silence phase.
        if (!speechStarted && rms < Math.max(0.03, noiseFloor * 1.8)) {
          noiseFloor = Math.min(0.04, Math.max(0.006, noiseFloor * 0.94 + rms * 0.06));
        }
        const startThreshold = Math.max(0.03, noiseFloor * 2.15);
        // Once speech has begun, use a lower release threshold. This keeps
        // quiet syllables and natural mid-sentence pauses from ending a turn.
        const activeThreshold = speechStarted
          ? Math.max(0.021, noiseFloor * 1.65)
          : startThreshold;
        if (rms >= activeThreshold) {
          silenceStartedAt = 0;
          if (!speechCandidateAt) speechCandidateAt = now;
          if (now - speechCandidateAt >= VAD_MIN_SPEECH_MS) speechStarted = true;
        } else {
          speechCandidateAt = 0;
          if (speechStarted) {
            if (!silenceStartedAt) silenceStartedAt = now;
            if (now - silenceStartedAt >= VAD_END_SILENCE_MS) {
              mediaRecorderRef.current?.stop();
              return;
            }
          } else if (now - startedAt >= VAD_NO_SPEECH_RESTART_MS) {
            vadNoSpeechRef.current = true;
            mediaRecorderRef.current?.stop();
            return;
          }
        }
      }
      levelFrameRef.current = requestAnimationFrame(measure);
    };
    levelFrameRef.current = requestAnimationFrame(measure);
  };

  useEffect(() => {
    getVoiceStatus().then((status) => setVoiceConnected(status.mode === "connected")).catch(() => setVoiceConnected(false));
    const hasRecorder = !!(window.MediaRecorder && navigator.mediaDevices?.getUserMedia);
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupportsSpeech(hasRecorder || !!SpeechRecognition);
  }, []);

  const browserSpeak = (text, runId) => new Promise((resolve, reject) => {
    if (!window.speechSynthesis) {
      reject(new Error("System speech is unavailable"));
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    const finish = (error) => {
      utterance.onstart = utterance.onend = utterance.onerror = null;
      window.clearTimeout(watchdog);
      if (utteranceRef.current === utterance) utteranceRef.current = null;
      if (playbackCancelRef.current === cancel) playbackCancelRef.current = null;
      if (speechRunRef.current === runId) setPlaybackActive(false);
      if (error) reject(error); else resolve();
    };
    const cancel = () => finish(speechAbortError());
    const watchdog = window.setTimeout(() => {
      const current = utteranceRef.current === utterance;
      finish(new Error("System speech stalled"));
      if (current) window.speechSynthesis.cancel();
    }, 60000);
    utterance.lang = "fi-FI";
    utterance.onstart = () => { if (speechRunRef.current === runId) setPlaybackActive(true); };
    utterance.onend = () => finish();
    utterance.onerror = (event) => finish(new Error(event.error || "System speech failed"));
    window.speechSynthesis.cancel();
    utteranceRef.current = utterance;
    playbackCancelRef.current = cancel;
    try { window.speechSynthesis.speak(utterance); } catch (error) { finish(error); }
  });

  const unlockAudio = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    try {
      const context = audioContextRef.current || new AudioContext();
      audioContextRef.current = context;
      if (context.state === "suspended") context.resume().catch(() => {});
      // Starting a silent buffer during the tap keeps later asynchronous TTS
      // playback available on iOS after recognition and network requests.
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, 22050);
      source.connect(context.destination);
      source.start(0);
    } catch {
      // HTML audio and system speech remain available as fallbacks.
    }
  };

  const stopPlayback = () => {
    speechRunRef.current += 1;
    speechAbortRef.current?.abort();
    speechAbortRef.current = null;
    playbackCancelRef.current?.();
    playbackCancelRef.current = null;
    setPlaybackActive(false);
    window.speechSynthesis?.cancel();
    utteranceRef.current = null;
    if (audioSourceRef.current) {
      try { audioSourceRef.current.stop(); } catch { /* already stopped */ }
      audioSourceRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
    stopLevelMeter();
    setSpeaking(false);
  };

  const playVoiceBlob = async (blob, runId) => {
    if (speechRunRef.current !== runId) return;
    const context = audioContextRef.current;
    if (context) {
      if (context.state === "suspended") await context.resume();
      const encoded = await blob.arrayBuffer();
      const decoded = await context.decodeAudioData(encoded.slice(0));
      if (speechRunRef.current !== runId) return;
      await new Promise((resolve, reject) => {
        const source = context.createBufferSource();
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.68;
        source.buffer = decoded;
        startLevelMeter(analyser);
        source.connect(analyser);
        analyser.connect(context.destination);
        levelSourceRef.current = source;
        const finish = (error) => {
          source.onended = null;
          window.clearTimeout(watchdog);
          if (playbackCancelRef.current === cancel) playbackCancelRef.current = null;
          if (speechRunRef.current === runId) {
            setPlaybackActive(false);
            stopLevelMeter();
          }
          if (audioSourceRef.current === source) audioSourceRef.current = null;
          try { source.disconnect(); analyser.disconnect(); } catch { /* already disconnected */ }
          if (error) {
            try { source.stop(); } catch { /* not started or already ended */ }
            reject(error);
          } else resolve();
        };
        const cancel = () => finish(speechAbortError());
        const watchdog = window.setTimeout(() => finish(Object.assign(new Error("Audio stalled"), {playbackStarted: true})), Math.max(15000, decoded.duration * 1000 + 5000));
        source.onended = () => finish();
        playbackCancelRef.current = cancel;
        audioSourceRef.current = source;
        try { source.start(0); setPlaybackActive(true); } catch (error) { finish(error); }
      });
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    const audio = new Audio(objectUrl);
    audioRef.current = audio;
    try {
      await new Promise((resolve, reject) => {
        let started = false;
        const finish = (error) => {
          audio.onplaying = audio.onended = audio.onerror = null;
          window.clearTimeout(watchdog);
          if (playbackCancelRef.current === cancel) playbackCancelRef.current = null;
          if (error) { audio.pause(); reject(Object.assign(error, {playbackStarted: started})); }
          else resolve();
        };
        const cancel = () => finish(speechAbortError());
        const watchdog = window.setTimeout(() => finish(new Error("Audio stalled")), 90000);
        playbackCancelRef.current = cancel;
        audio.onplaying = () => { started = true; if (speechRunRef.current === runId) setPlaybackActive(true); };
        audio.onended = () => finish();
        audio.onerror = () => finish(new Error("Audio playback failed"));
        try { audio.play().catch(finish); } catch (error) { finish(error); }
      });
    } finally {
      if (speechRunRef.current === runId) setPlaybackActive(false);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      URL.revokeObjectURL(objectUrl);
      if (audioRef.current === audio) audioRef.current = null;
    }
  };

  const speak = async (text) => {
    if (!speakerOnRef.current) return;
    stopPlayback();
    const runId = speechRunRef.current + 1;
    speechRunRef.current = runId;
    const controller = new AbortController();
    speechAbortRef.current = controller;
    setVoiceNotice("");
    setSpeaking(true);
    try {
      await playSpeech({ text, connected: voiceConnected, synthesize: synthesizeVoice,
        play: blob => playVoiceBlob(blob, runId), browser: chunk => browserSpeak(chunk, runId),
        signal: controller.signal,
        onFallback: () => { if (voiceConnected && speechRunRef.current === runId) setVoiceNotice("Äänipalvelu ei vastannut. Jatkan laitteen omalla puheäänellä."); },
      });
    } catch (error) {
      if (speechRunRef.current !== runId) return;
      setVoiceNotice("Äänentoisto katkesi tai estyi. Koko vastaus näkyy tekstinä. Tarkista laitteen äänenvoimakkuus; voit pyytää vastauksen uudelleen ääneen.");
    } finally {
      controller.abort();
      if (speechAbortRef.current === controller) speechAbortRef.current = null;
      if (speechRunRef.current === runId) { setSpeaking(false); setPlaybackActive(false); }
    }
  };

  const showNextAction = async (text) => {
    const sessionRun = sessionRunRef.current;
    setMessages((current) => [...current, { role: "user", time: "nyt", text }]);
    setTranscript("");
    setThinking(true);
    try {
      const result = await getNextAction();
      if (sessionRunRef.current !== sessionRun) return;
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: result.message,
        emailTaskReview:result.email_task_review,
      }]);
      setThinking(false);
      await speak(result.speech_response || result.message);
    } catch {
      if (sessionRunRef.current !== sessionRun) return;
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: "En saanut kalenteria ja tehtäviä juuri nyt. Yritä hetken kuluttua uudelleen.",
      }]);
    } finally {
      if (sessionRunRef.current === sessionRun) setThinking(false);
    }
  };

  const recordAttentionAcknowledgement = (source) => {
    setAcknowledgedAttention(`${workContext.target_id}:${source.attention_fingerprint}`);
    setPendingActions([]);pendingCreatedAtRef.current=0;proposalRef.current=null;shownProposal.current=null;
    setProposalExpired(false);setActionError("");setSourceReload(value=>value+1);
    setMessages(current=>[...current,{role:"assistant",time:"nyt",text:attentionMessage}]);
    if(initialSolution?.viewRevision)window.dispatchEvent(new CustomEvent("acai-work-solution-saved",{detail:{reference:workContext,viewRevision:initialSolution.viewRevision,message:attentionMessage,actionType:"email_attention_ack"}}));
  };

  const submit = async (text, files = []) => {
    if (!text.trim() || requestBusyRef.current || actionSubmissionRef.current) return false;
    if(workContext&&(!selectedSource||sourceError))return false;
    setVoiceIssue(null);
    const decision = pendingActions.length && !files.length ? voiceDecision(text) : null;
    if (decision) {
      setMessages((current) => [...current, { role: "user", time: "nyt", text }]);
      setTranscript("");
      if (decision === "approve") await confirmActions(shownProposal.current);
      else await cancelActions();
      return;
    }
    if (!scoped && !knowledgeMode && !files.length && isNextActionRequest(text)) {
      requestBusyRef.current = true;
      const session = sessionRunRef.current;
      try { await showNextAction(text); } finally { if (sessionRunRef.current === session) requestBusyRef.current = false; }
      return;
    }
    requestBusyRef.current = true;
    // A correction invalidates the old proposal before any network request.
    setPendingActions([]);
    proposalRef.current = null;
    shownProposal.current = null;
    pendingCreatedAtRef.current = 0;
    setFailedRequest("");
    const sessionRun = sessionRunRef.current;
    const general = !scoped&&(knowledgeMode || files.length > 0);
    const history = messages.filter(message => Boolean(message.general) === Boolean(general)).slice(-8).map((message) => ({ role: message.role, content: (message.modelText || message.text).slice(0,3000) }));
    const visibleText = files.length ? `${text}\n\n${files.map((file,index) => `Liite ${index+1}: ${file.name}`).join("\n")}` : text;
    setMessages((current) => [...current, { role: "user", time: "nyt", text:visibleText, modelText:text, general }]);
    setTranscript("");
    setThinking(true);
    let savingAttention=false;
    try {
      const result = workContext ? await runAgent(text,history,{workContext}) : emailTaskSuggestionId ? await runAgent(text,history,{emailTaskSuggestionId}) : collaborationRequestId
        ? await runAgent(text, history, {requestId:collaborationRequestId,summaryOnly:text === initialPrompt})
        : general ? await runAgent(text, history, null, {files,allowWeb,attachmentConsent}) : await runAgent(text, history);
      // Memory can be saved even if the sheet was closed while waiting.
      if (result.memory_changes?.length) notifyDataChanged(["memory"]);
      if (sessionRunRef.current !== sessionRun) return;
      const currentSource=workContext?await getWorkContext(workContext):null;
      if(sessionRunRef.current!==sessionRun)return;
      if(currentSource)setSelectedSource(currentSource);
      if(workContext?.kind==="collaboration_request"&&(
        result.context_info?.reference?.kind!==workContext.kind||result.context_info?.reference?.target_id!==workContext.target_id||
        result.context_info?.source_fingerprint!==currentSource?.source_fingerprint||(result.proposed_actions||[]).length)){
        throw new Error("Yhteistyöpyyntö muuttui tai vastausta ei voitu varmistaa. Tarkista nykyinen tilanne ja kysy uudelleen.");
      }
      const bound=workContext?(result.proposed_actions||[]).filter(action=>solutionActionAllowed(action,workContext,currentSource)):[];
      const sourceActions=bound.length===1?bound:[];
      // Only the reversible, local attention decision may complete directly.
      // Never auto-execute a preparation prompt, transferred proposal or mixed actions.
      if(workContext?.kind==="email"&&!files.length&&text!==initialPrompt&&result.proposed_actions?.length===1&&sourceActions[0]?.type==="email_attention_ack"){
        savingAttention=true;
        actionSubmissionRef.current=true;setAddingActions(true);
        try{
          await setEmailAttention(workContext.target_id,currentSource.attention_fingerprint);
          if(sessionRunRef.current!==sessionRun)return;
          recordAttentionAcknowledgement(currentSource);
          setThinking(false);await speak(attentionMessage);
          return true;
        }finally{actionSubmissionRef.current=false;setAddingActions(false);}
      }
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: result.knowledge_info ? `${result.response}\n\nYleistieto · ${result.knowledge_info.web_checked ? "Verkkolähteitä käytetty" : "Ei verkkotarkistusta"} · Sähköpostia, kalenteria tai muistia ei luettu.${files.length ? " Liitteet koskevat tätä viestiä; liitä ne uudelleen, jos tarvitset uuden kohdan tarkistusta." : ""}` : result.response,
        ...(emailTaskSuggestionId?{modelText:`${result.response}\nTarkistettava, ei hyväksytty tehtäväluonnos: ${JSON.stringify((result.proposed_actions||[]).filter(action=>action.type==="email_task_accept"&&action.suggestion_id===emailTaskSuggestionId).map(({title,estimated_minutes,priority})=>({title,estimated_minutes,priority})))}`} : {}),
        ...(workContext?{modelText:solutionModelText(result.response,sourceActions)}:{}),
        ...(workContext?{solution_summary:result.solution_summary}:{}),
        general,
        sources: result.sources || result.news_sources || [],
        memoryCount: result.memory_changes?.length || 0,
        memoryFailed: Boolean(result.memory_failed),
      }]);
      const candidates = (workContext?sourceActions:result.proposed_actions || []).filter(action => workContext
        ? true
        : emailTaskSuggestionId
        ? action.type === "email_task_accept" && action.suggestion_id === emailTaskSuggestionId
        : collaborationRequestId
        ? action.type === "collaboration_response" && action.request_id === collaborationRequestId
        : general ? ["task","reminder","calendar_event"].includes(action.type) : !["collaboration_response","email_task_accept","email_attention_ack"].includes(action.type));
      const actions=(workContext&&candidates.length>1?[]:candidates).map((action, index) => ({
        ...action,
        client_action_id: action.client_action_id || (
          typeof globalThis.crypto?.randomUUID === "function"
            ? globalThis.crypto.randomUUID()
            : `voice-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`
        ),
      }));
      setPendingActions(actions);
      if(workContext){setSolutionPlanning(result.planning||null);if(actions.length)setSolutionRevision(value=>value+1);}
      setProposalExpired(false);
      setProposalKind(actions.length ? (result.proposal_kind || "") : "");
      setActionError("");
      pendingCreatedAtRef.current = actions.length ? Date.now() - Math.max(0, Number(result.proposal_age_seconds) || 0) * 1000 : 0;
      setThinking(false);
      if (files.length) {setAttachmentFiles([]); setAttachmentConsent(false);}
      await speak(result.speech_response || result.response);
      return true;
    } catch (error) {
      if (sessionRunRef.current !== sessionRun) return;
      const message = savingAttention?userError(error,"Kuittausta ei voitu vahvistaa. Tarkista asian tila ennen uutta yritystä."):agentErrorMessage(error);
      const lostRequest=workContext?.kind==="collaboration_request"&&[403,404,409].includes(error?.response?.status);
      if(lostRequest){setSelectedSource(null);setSourceError(true);setPendingActions([]);}
      setFailedRequest(text);
      setVoiceIssue({ stage: savingAttention?"Kuittaus":"Assistenttiyhteys", message });
      setMessages((current) => [...(lostRequest?[]:current), { role: "assistant", time: "nyt", text: message, general }]);
      return false;
    } finally {
      if (sessionRunRef.current === sessionRun) { requestBusyRef.current = false; setThinking(false); }
    }
  };

  useEffect(()=>{
    if(!open||!emailTaskSuggestionId)return;
    let active=true;
    setThinking(true);setPendingActions([]);pendingCreatedAtRef.current=0;setFailedRequest("");
    getEmailTaskSuggestions(emailTaskSuggestionId).then(page=>{
      if(!active)return;
      const item=page.items.find(row=>row.id===emailTaskSuggestionId);
      if(!item)throw new Error("Ehdotus on muuttunut tai jo käsitelty. Avaa Tehtävät-sivu uudelleen.");
      setPendingActions([emailTaskAction(item)]);
      pendingCreatedAtRef.current=Date.now();setProposalExpired(false);
      setMessages([{role:"assistant",time:"nyt",text:"Tarkista tehtäväehdotus. Voit hyväksyä sen puheella tai tekstillä tai tarkentaa sitä omin sanoin. Tarkennus tarvitsee tekoäly-yhteyden; pelkkä tallennus ei käytä Relevance-krediittejä."}]);
    }).catch(error=>{if(active)setMessages([{role:"assistant",time:"nyt",text:userError(error,"Ehdotusta ei saatu. Avaa se uudelleen Tehtävät-sivulta.")}]);})
      .finally(()=>{if(active)setThinking(false);});
    return()=>{active=false;};
  },[open,emailTaskSuggestionId,emailTaskReload]);

  useEffect(() => {
    if (!initialPrompt) {
      automaticPromptRef.current = "";
      return;
    }
    if (!open || automaticPromptRef.current === initialPrompt) return;
    automaticPromptRef.current = initialPrompt;
    submit(initialPrompt);
  // submit intentionally uses the current voice and conversation state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, open]);

  async function confirmActions(expectedSignature) {
    if(workContext&&(!selectedSource||sourceError))return;
    // React state updates are asynchronous. The ref prevents a spoken
    // approval and a tap from starting the same external mutation twice.
    if (!pendingActions.length || actionSubmissionRef.current || requestBusyRef.current || !expectedSignature ||
        expectedSignature !== shownProposal.current || expectedSignature !== proposalRef.current?.signature ||
        !pendingActions.every(previewReady)) return;
    if (!pendingCreatedAtRef.current || Date.now() - pendingCreatedAtRef.current > PENDING_ACTION_TTL_MS) {
      const expired = "Ehdotus ehti vanhentua. Pyydä minua muodostamaan se uudelleen.";
      setPendingActions([]);
      setProposalExpired(false);
      setProposalKind("");
      setActionError("");
      pendingCreatedAtRef.current = 0;
      setMessages((current) => [...current, { role: "assistant", time: "nyt", text: expired }]);
      await speak(expired);
      return;
    }
    actionSubmissionRef.current = true;
    setAddingActions(true);
    setActionError("");
    const actionsToConfirm = [...pendingActions];
    let publishedCalendarEvents = 0;
    let localCalendarDrafts = 0;
    let emailDrafts = 0;
    let collaborationRequests = 0;
    let collaborationResponses = 0;
    let completedActions = 0;
    const changedResources = new Set();
    const refreshChanged = () => {
      notifyDataChanged([...changedResources]);
      changedResources.clear();
    };
    try {
      for (const action of actionsToConfirm) {
        // Also refresh after an ambiguous failure: a draft or remote write may
        // have succeeded before a later step (or its HTTP response) failed.
        actionResources(action.type).forEach(resource => changedResources.add(resource));
        if (action.type === "calendar_event") {
          const draft = await draftEvent({
            title: action.title,
            start: action.start,
            end: action.end,
            location: action.location || "",
            reminder_minutes: action.reminder_minutes ?? 30,
            idempotency_key: action.client_action_id,
          });
          try {
            const approval = await createApprovalToken({
              action: "calendar_mutation",
              resource_id: draft.id,
            });
            await publishEvent(draft.id, approval.token);
            publishedCalendarEvents += 1;
          } catch {
            localCalendarDrafts += 1;
          }
        } else if (action.type === "calendar_event_update") {
          const changes = {};
          if(action.source_context?.reference?.kind==="email"){
            changes.origin_email_id=action.source_context.reference.target_id;
            changes.origin_email_fingerprint=action.source_context.source_fingerprint;
          }
          if(action.expected_source_fingerprint)changes.expected_source_fingerprint=action.expected_source_fingerprint;
          if (action.title !== undefined) changes.title = action.title;
          if (action.start !== undefined) changes.start = action.start;
          if (action.end !== undefined) changes.end = action.end;
          if (action.location !== undefined) changes.location = action.location;
          if (action.reminder_minutes !== undefined) changes.reminder_minutes = action.reminder_minutes;
          const approval = await createApprovalToken({
            action: "calendar_mutation",
            resource_id: action.target_id,
          });
          await updateCalendarEvent(action.target_id, { ...changes, approval_token: approval.token });
        } else if (action.type === "reminder") {
          await createReminder({
            title: action.title,
            remind_at: action.remind_at,
            recurrence: action.recurrence || "none",
            idempotency_key: action.client_action_id,
          });
        } else if (action.type === "task") {
          await createTask({
            title: action.title,
            priority: action.priority || "medium",
            due: action.due || null,
            visibility: action.visibility || "personal",
            idempotency_key: action.client_action_id,
          });
        } else if (action.type === "email_task_accept") {
          if(!emailTaskSuggestionId||action.suggestion_id!==emailTaskSuggestionId)throw new Error("Virheellinen tehtäväkonteksti");
          await acceptEmailTaskSuggestion(action.suggestion_id,{title:action.title,estimated_minutes:action.estimated_minutes,priority:action.priority,source_fingerprint:action.source_fingerprint});
        } else if (action.type === "task_update") {
          const changes = {};
          if(action.expected_source_fingerprint)changes.expected_source_fingerprint=action.expected_source_fingerprint;
          if (action.title !== undefined) changes.title = action.title;
          if (action.status !== undefined) changes.status = action.status;
          if (action.priority !== undefined) changes.priority = action.priority;
          if (action.due !== undefined) changes.due = action.due;
          await updateTask(action.target_id, changes);
        } else if (action.type === "reminder_update") {
          const changes = {};
          if (action.title !== undefined) changes.title = action.title;
          if (action.done !== undefined) changes.done = action.done;
          if (action.remind_at !== undefined) changes.remind_at = action.remind_at;
          if (action.recurrence !== undefined) changes.recurrence = action.recurrence;
          await updateReminder(action.target_id, changes);
        } else if (action.type === "email_attention_ack") {
          if(workContext?.kind!=="email"||action.email_id!==workContext.target_id)throw new Error("Virheellinen kuittauskonteksti");
          await setEmailAttention(action.email_id,action.attention_fingerprint);
        } else if (action.type === "email_draft") {
          await draftReply(action.email_id, action.body,action.expected_source_fingerprint,...(action.background_token?[action.background_token]:[]));
          emailDrafts += 1;
        } else if (action.type === "collaboration_request") {
          const draft = await createCollaborationDraft({
            kind: action.kind, title: action.title, body: action.body,
            recipient_ids: action.recipient_ids, idempotency_key: action.client_action_id,
            ...(action.start?{start:action.start,end:action.end}:{}),
            ...(action.estimated_minutes!==undefined?{estimated_minutes:action.estimated_minutes}:{}),
            ...(action.meeting_slots?.length ? {meeting_slots:action.meeting_slots} : {}),
          });
          await sendCollaboration(draft.id);
          collaborationRequests += 1;
        } else if (action.type === "collaboration_response") {
          if (!collaborationRequestId || action.request_id !== collaborationRequestId) throw new Error("Invalid collaboration context");
          await respondCollaboration(action.request_id, {decision:action.decision,body:action.body,
            ...(action.expected_revision?{expected_revision:action.expected_revision}:{}),
            ...(action.available_slot_ids?.length ? {available_slot_ids:action.available_slot_ids} : {})});
          collaborationResponses += 1;
        } else if (action.type === "memory") {
          await createMemory({
            content: action.content,
            category: action.category || "other",
            idempotency_key: action.client_action_id,
          });
        } else if (action.type === "monitor") {
          await createMonitor({
            name: action.name,
            kind: action.kind,
            frequency_minutes: action.frequency_minutes || 1440,
            enabled: action.enabled !== false,
            idempotency_key: action.client_action_id,
          });
        } else if (action.type === "monitor_update") {
          const changes = {};
          if (action.enabled !== undefined) changes.enabled = action.enabled;
          if (action.frequency_minutes !== undefined) changes.frequency_minutes = action.frequency_minutes;
          await updateMonitor(action.target_id, changes);
        } else {
          throw new Error("Unsupported assistant action");
        }
        completedActions += 1;
      }
      refreshChanged();
      if(workContext)setSourceReload(value=>value+1);
      let confirmation = `${actionsToConfirm.length} toimintoa tallennettiin.`;
      if(actionsToConfirm.every(action=>action.type==="email_task_accept"))confirmation="Tehtävä lisättiin omaan tehtävälistaasi. Sähköpostia tai kalenteria ei muutettu.";
      if (publishedCalendarEvents && !localCalendarDrafts) {
        confirmation = publishedCalendarEvents === 1
          ? "Hyväksytty. Tapahtuma lisättiin Google Kalenteriin."
          : `Hyväksytty. ${publishedCalendarEvents} tapahtumaa lisättiin Google Kalenteriin.`;
      } else if (localCalendarDrafts) {
        confirmation = "Hyväksytty ja tallennettu luonnokseksi, mutta Google Kalenteriin lisääminen ei onnistunut. Luonnos löytyy Kalenteri-näkymästä.";
      } else if (emailDrafts === actionsToConfirm.length) {
        confirmation = emailDrafts === 1
          ? "Hyväksytty. Sähköpostivastaus tallennettiin luonnokseksi, mutta sitä ei lähetetty."
          : `Hyväksytty. ${emailDrafts} sähköpostivastausta tallennettiin luonnoksiksi, mutta niitä ei lähetetty.`;
      }
      if (collaborationRequests) confirmation += ` ${collaborationRequests} sisäistä yhteistyöpyyntöä jaettiin hyväksymillesi vastaanottajille. Vastaukset löytyvät Yhteistyö-näkymästä. Tämä ei varaa kalenteria.`;
      if (collaborationResponses) confirmation = "Vastaus jaettiin pyynnön osallistujille. Kalenteria ei muutettu.";
      if(workContext&&actionsToConfirm.length===1&&actionsToConfirm[0].type==="calendar_event_update")confirmation="Hyväksymäsi kalenterimuutos tallennettiin. Erillistä sähköpostivastausta ei lähetetty.";
      if(workContext&&actionsToConfirm.length===1&&actionsToConfirm[0].type==="email_attention_ack")confirmation=attentionMessage;
      if(workContext?.kind==="email"&&initialSolution?.viewRevision)window.dispatchEvent(new CustomEvent("acai-work-solution-saved",{detail:{reference:workContext,viewRevision:initialSolution.viewRevision,message:confirmation,actionType:actionsToConfirm[0]?.type}}));
      setPendingActions([]);
      setProposalExpired(false);
      setProposalKind("");
      setActionError("");
      pendingCreatedAtRef.current = 0;
      setMessages((current) => [...current, { role: "assistant", time: "nyt", text: confirmation,...(workContext?{modelText:solutionModelText(confirmation,actionsToConfirm,"saved")}:{} ) }]);
      await speak(confirmation);
    } catch (error) {
      refreshChanged();
      const remainingActions = actionsToConfirm.slice(completedActions);
      const invalidSource=workContext&&[403,404,409].includes(error?.response?.status);
      setPendingActions(workContext?[]:remainingActions);
      if(invalidSource){setSourceError(true);setSelectedSource(null);}
      const progress = completedActions > 0
        ? `${completedActions} toimintoa tallennettiin. ${remainingActions.length} jäi odottamaan. `
        : "";
      const message = progress + userError(error, "Toiminnon tallentaminen ei onnistunut. Tarkista tallennuksen tila ja yritä uudelleen.");
      setActionError(message);
      if(workContext)setMessages(current=>[...current,{role:"assistant",time:"nyt",text:`${message} Vanha ehdotus poistettiin käytöstä. Tarkista lähdetiedot ja pyydä uusi ratkaisu ennen uutta hyväksyntää.`}]);
      await speak(message);
    } finally {
      refreshChanged();
      actionSubmissionRef.current = false;
      setAddingActions(false);
    }
  }

  async function cancelActions() {
    if (!pendingActions.length || actionSubmissionRef.current) return;
    const confirmation = "Selvä, peruin ehdotuksen.";
    setPendingActions([]);
    setProposalExpired(false);
    setProposalKind("");
    setActionError("");
    pendingCreatedAtRef.current = 0;
    setMessages((current) => [...current, { role: "assistant", time: "nyt", text: confirmation,...(workContext?{modelText:solutionModelText(confirmation,[],"cancelled")}:{} ) }]);
    await speak(confirmation);
  }

  const editPendingRequest = () => {
    if (addingActions || actionSubmissionRef.current) return;
    const latestRequest = [...messages].reverse().find((message) => message.role === "user")?.text || "";
    stopConversation();
    setPendingActions([]);
    setProposalExpired(false);
    setProposalKind("");
    setActionError("");
    pendingCreatedAtRef.current = 0;
    setDraftMessage(latestRequest);
    setMessages((current) => [...current, {
      role: "assistant",
      time: "nyt",
      text: "Muokkaa pyyntöä alla ja lähetä se uudelleen. Avoin toimintoehdotus peruttiin. Aiemmat tallennukset ja automaattisen muistin päivitykset eivät peruunnu.",
    }]);
    window.setTimeout(() => {
      composerRef.current?.focus();
      composerRef.current?.setSelectionRange(latestRequest.length, latestRequest.length);
    }, 0);
  };

  const startBrowserListening = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMessages((current) => [...current, { role: "assistant", time: "nyt", text: "Puheentunnistus ei ole käytettävissä tällä laitteella." }]);
      return;
    }
    if (recognitionRef.current || listening) return;
    setCaptureStatus("preparing");
    stopPlayback();
    setVoiceNotice("");
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (error) {
      conversationActiveRef.current = false;
      setConversationActive(false);
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: "Mikrofonin käyttöä ei ole sallittu. Salli mikrofoni selaimen sivustoasetuksista ja yritä uudelleen.",
      }]);
      setCaptureStatus("idle");
      return;
    }
    if (!conversationActiveRef.current) {
      setCaptureStatus("idle");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "fi-FI";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const nextTranscript = Array.from(event.results).map((result) => result[0].transcript).join("");
      transcriptRef.current = nextTranscript;
      setTranscript(nextTranscript);
    };
    recognition.onerror = (event) => {
      recognitionErrorRef.current = true;
      setListening(false);
      setCaptureStatus("idle");
      if (event.error === "no-speech") return;
      conversationActiveRef.current = false;
      setConversationActive(false);
      const errorMessages = {
        "not-allowed": "Mikrofonin käyttöä ei ole sallittu. Salli mikrofoni selaimen sivustoasetuksista ja yritä uudelleen.",
        "service-not-allowed": "Selaimen puheentunnistus on estetty. Tarkista sivuston mikrofoni- ja puheentunnistusluvat.",
        "audio-capture": "Mikrofonia ei löytynyt. Tarkista laitteen mikrofoniasetukset.",
        network: "Puheentunnistus ei saanut verkkoyhteyttä. Yritä hetken kuluttua uudelleen.",
      };
      const message = errorMessages[event.error] || "Puheentunnistus keskeytyi. Yritä uudelleen.";
      setVoiceIssue({ stage: "Puheentunnistus", message });
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: message,
      }]);
    };
    recognition.onend = () => {
      setListening(false);
      setCaptureStatus("idle");
      recognitionRef.current = null;
      const finalTranscript = transcriptRef.current.trim();
      if (discardRecordingRef.current) {
        discardRecordingRef.current = false;
        transcriptRef.current = "";
        setTranscript("");
        return;
      }
      if (!recognitionErrorRef.current && finalTranscript) submit(finalTranscript);
      else if (!recognitionErrorRef.current && !finalTranscript && !conversationActiveRef.current) {
        setMessages((current) => [...current, {
          role: "assistant",
          time: "nyt",
          text: "En kuullut puhetta. Napauta mikrofonia, puhu ja lopeta napauttamalla uudelleen.",
        }]);
      }
    };
    recognitionRef.current = recognition;
    recognitionErrorRef.current = false;
    transcriptRef.current = "";
    setListening(true);
    setCaptureStatus("listening");
    setTranscript("");
    try {
      recognition.start();
    } catch (error) {
      recognitionRef.current = null;
      setListening(false);
      setCaptureStatus("idle");
    }
  };

  const startListening = async () => {
    if (mediaRecorderRef.current || recognitionRef.current || listening) return;
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      await startBrowserListening();
      return;
    }
    try {
      setCaptureStatus("preparing");
      stopPlayback();
      setVoiceNotice("");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      if (!conversationActiveRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        setCaptureStatus("idle");
        return;
      }
      const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"]
        .find((type) => window.MediaRecorder.isTypeSupported?.(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = audioContextRef.current || (AudioContext ? new AudioContext() : null);
      if (context) {
        audioContextRef.current = context;
        if (context.state === "suspended") await context.resume();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.58;
        startLevelMeter(analyser, { detectSpeech: true });
        source.connect(analyser);
        levelSourceRef.current = source;
      }
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      discardRecordingRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) audioChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        window.clearTimeout(recordingTimeoutRef.current);
        conversationActiveRef.current = false;
        setConversationActive(false);
        stopLevelMeter();
        setListening(false);
        setCaptureStatus("idle");
        mediaRecorderRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        const message = "Äänen tallennus keskeytyi. Yritä uudelleen.";
        setVoiceIssue({ stage: "Mikrofoni", message });
        setMessages((current) => [...current, { role: "assistant", time: "nyt", text: message }]);
      };
      recorder.onstop = async () => {
        window.clearTimeout(recordingTimeoutRef.current);
        stopLevelMeter();
        setListening(false);
        mediaRecorderRef.current = null;
        mediaStreamRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          audioChunksRef.current = [];
          vadNoSpeechRef.current = false;
          setCaptureStatus("idle");
          return;
        }
        if (vadNoSpeechRef.current) {
          vadNoSpeechRef.current = false;
          setCaptureStatus("idle");
          return;
        }
        setCaptureStatus("transcribing");
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        audioChunksRef.current = [];
        if (blob.size < 500) {
          setCaptureStatus("idle");
          if (!conversationActiveRef.current) {
            setMessages((current) => [...current, { role: "assistant", time: "nyt", text: "En kuullut puhetta. Aloita keskustelu ja puhu normaalisti." }]);
          }
          return;
        }
        try {
          setTranscript("Tunnistan puhetta…");
          const recordedType = recorder.mimeType || blob.type || "audio/webm";
          const filename = recordedType.includes("mp4") || recordedType.includes("m4a")
            ? "speech.m4a"
            : recordedType.includes("ogg")
              ? "speech.ogg"
              : "speech.webm";
          const result = await transcribeVoice(blob, filename);
          setVoiceIssue(null);
          transcriptRef.current = result.text;
          setTranscript(result.text);
          setCaptureStatus("idle");
          await submit(result.text);
        } catch (error) {
          conversationActiveRef.current = false;
          setConversationActive(false);
          setTranscript("");
          setCaptureStatus("idle");
          const message = userError(error, "Puheentunnistus ei onnistunut. Yritä uudelleen.");
          setVoiceIssue({ stage: "Puheentunnistus", message });
          setMessages((current) => [...current, { role: "assistant", time: "nyt", text: message }]);
        }
      };
      setTranscript("");
      vadNoSpeechRef.current = false;
      setListening(true);
      setCaptureStatus("listening");
      recorder.start(250);
      recordingTimeoutRef.current = window.setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          // A hard limit must still submit recorded speech. Marking this as
          // "no speech" used to silently discard a turn when auto-stop failed.
          mediaRecorderRef.current.stop();
        }
      }, 45000);
    } catch (error) {
      conversationActiveRef.current = false;
      setConversationActive(false);
      setListening(false);
      setCaptureStatus("idle");
      const message = "Mikrofonin käyttöä ei ole sallittu. Salli mikrofoni selaimen sivustoasetuksista ja yritä uudelleen.";
      setVoiceIssue({ stage: "Mikrofoni", message });
      setMessages((current) => [...current, {
        role: "assistant",
        time: "nyt",
        text: message,
      }]);
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      return;
    }
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (error) { /* already stopped */ } }
    if (captureStatus === "preparing") setCaptureStatus("idle");
  };

  useEffect(() => {
    if (!open || !conversationActive || listening || captureStatus !== "idle" || thinking || speaking || addingActions) return undefined;
    const restart = window.setTimeout(() => {
      if (conversationActiveRef.current) startListening();
    }, 180);
    return () => window.clearTimeout(restart);
  // startListening intentionally uses the latest refs and state snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationActive, listening, captureStatus, thinking, speaking, addingActions]);

  const startConversation = () => {
    unlockAudio();
    if (listening) {
      // Manual fallback: finish and submit this turn without ending the
      // continuous conversation. Automatic listening resumes after the reply.
      discardRecordingRef.current = false;
      vadNoSpeechRef.current = false;
      stopListening();
      return;
    }
    if (speaking) {
      stopPlayback();
      return;
    }
    if (conversationActiveRef.current || captureStatus !== "idle" || thinking || addingActions) return;
    discardRecordingRef.current = false;
    conversationActiveRef.current = true;
    setConversationActive(true);
  };

  const stopConversation = () => {
    conversationActiveRef.current = false;
    setConversationActive(false);
    discardRecordingRef.current = true;
    stopListening();
    stopPlayback();
    setTranscript("");
    setCaptureStatus("idle");
  };

  const sendTypedMessage = async (event) => {
    event.preventDefault();
    const text = draftMessage.trim();
    if (!text || thinking || addingActions || captureStatus === "transcribing") return;
    if(workContext&&(!selectedSource||sourceError))return;
    if (attachmentFiles.length && !attachmentConsent) {setAttachmentError("Hyväksy liitteiden lähettäminen ennen lähettämistä."); return;}

    // Switching to typing must not leave the microphone recording in the
    // background or submit the same turn twice after the typed message.
    if (conversationActiveRef.current || listening || speaking) stopConversation();
    setDraftMessage("");
    await submit(text, attachmentFiles);
  };

  useEffect(()=>{
    if(!open){autoVoiceStarted.current=false;return;}
    if(!autoStartVoice||autoVoiceStarted.current||workContext&&!selectedSource||sourceError||thinking||addingActions)return;
    autoVoiceStarted.current=true;
    startConversation();
  // Start once only after the selected source is available. Normal opens never record.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open,autoStartVoice,selectedSource,sourceError,thinking,addingActions]);

  const endSession = () => {
    if (actionSubmissionRef.current) { setVoiceNotice("Tallennus on kesken. Odota sen valmistumista ennen sulkemista."); return; }
    sessionRunRef.current += 1;
    requestBusyRef.current = false;
    stopPlayback();
    window.clearTimeout(recordingTimeoutRef.current);
    conversationActiveRef.current = false;
    setConversationActive(false);
    discardRecordingRef.current = true;
    stopLevelMeter();
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      try { recognitionRef.current.abort(); } catch (error) { /* already stopped */ }
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state === "recording") mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    audioChunksRef.current = [];
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    setListening(false);
    setThinking(false);
    setCaptureStatus("idle");
    setSpeaking(false);
    setVoiceNotice("");
    setVoiceIssue(null);
    setAttachmentFiles([]);
    setAttachmentConsent(false);
    setAttachmentError("");
    setKnowledgeMode(false);
    setAllowWeb(false);
    // Draft and visible history stay in this signed-in Layout's memory only.
    setPendingActions([]);
    setProposalExpired(false);
    setProposalKind("");
    setActionError("");
    actionSubmissionRef.current = false;
    pendingCreatedAtRef.current = 0;
    onOpenChange(false);
  };

  const toggleSpeaker = () => {
    unlockAudio();
    if (speakerOn) {
      stopPlayback();
      speakerOnRef.current = false;
      setSpeakerOn(false);
      return;
    }
    setVoiceNotice("");
    speakerOnRef.current = true;
    setSpeakerOn(true);
  };

  const statusLabel = captureStatus === "preparing"
    ? "Valmistelen mikrofonia…"
    : captureStatus === "transcribing"
      ? "Tunnistan puhetta…"
      : listening
        ? "Kuuntelen…"
        : thinking
          ? "Ajattelen…"
          : speaking
            ? (playbackActive ? "Puhun…" : "Valmistelen ääntä…")
            : conversationActive
              ? "Odotan puhetta…"
              : "Valmiina";

  const primaryControlLabel = listening
    ? "Lähetä puhe nyt"
    : speaking
      ? "Keskeytä vastaus"
      : captureStatus === "transcribing" || thinking || addingActions
        ? "Odota hetki…"
        : conversationActive
          ? "Kuuntelu jatkuu"
          : "Aloita keskustelu";
  const primaryControlDisabled = captureStatus === "preparing" || captureStatus === "transcribing" || thinking || addingActions || attachmentFiles.length > 0 || (conversationActive && !listening && !speaking);

  const waveFactors = [.22, .34, .48, .64, .78, .9, 1, 1.08, 1, .9, .78, .64, .48, .34, .22];
  const voiceMode = listening ? "listening" : playbackActive ? "speaking" : (thinking || speaking || captureStatus === "transcribing") ? "thinking" : "idle";

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : endSession())}>
      <SheetContent side="bottom" hideClose data-testid="voice-sheet" style={assistantViewport.style} className={cn("voice-sheet p-0 w-full max-w-[480px] mx-auto h-[100dvh] flex flex-col gap-0 overflow-hidden border-0", assistantViewport.keyboardOpen && "is-keyboard-open")}>
        <SheetTitle className="sr-only">Assistentti</SheetTitle>
        <SheetDescription className="sr-only">Keskustele assistentin kanssa puhumalla tai kirjoittamalla. Tallennettavat toiminnot vaativat hyväksynnän.</SheetDescription>
        <div className="voice-scroll hide-scrollbar">
          <header className="voice-header">
            <button type="button" data-testid="voice-close-btn" disabled={addingActions} onClick={endSession} className="voice-close-button" aria-label="Sulje assistentti"><span /><span /></button>
            <AcaiLogo compact />
            <div className="voice-header-spacer" />
          </header>
          <section className="voice-title-block">
            <h2>Assistentti</h2>
            <p>Puhu tai kirjoita — minä hoidan.</p>
            {workContext&&<div className="mx-auto mt-3 max-w-md rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm" aria-live="polite">
              {selectedSource?<><strong className="block break-words">{selectedSource.title}</strong><span className="mt-1 block text-xs text-muted-foreground">{workContext.kind==="collaboration_request"?"Lähettämäsi yhteistyöpyyntö ja sen jaetut vastaukset · keskustelu ei muuta tai lähetä pyyntöä.":<>Valittu oma {workContext.kind==="task"?"tehtävä":workContext.kind==="email"?"sähköposti ja omat kalenteriehdokkaat":"tapahtuma"} · muutokset vasta hyväksynnästäsi.</>}</span></>:<span>{sourceError?"Valittua asiaa ei voitu avata. Se on voinut muuttua tai käyttöoikeus päättyä.":"Avataan valittua asiaa…"}</span>}
              {sourceError&&<button type="button" className="min-h-11 text-primary underline" onClick={()=>setSourceReload(value=>value+1)}>Yritä avata uudelleen</button>}
              {workContext.kind==="collaboration_request"&&selectedSource&&!sourceError&&<a className="inline-flex min-h-11 items-center text-primary underline" href={`/collaboration?request=${encodeURIComponent(workContext.target_id)}`}>Avaa pyyntö ja vastaukset</a>}
              {workContext.kind==="email"&&selectedSource&&!sourceError&&<div className="mt-3">{acknowledgedAttention===`${workContext.target_id}:${selectedSource.attention_fingerprint}`?<p role="status">Kuitattu · ei tarvitse huomiota.</p>:<EmailAttentionButton emailId={workContext.target_id} sourceFingerprint={selectedSource.attention_fingerprint} disabled={thinking||addingActions||requestBusyRef.current} onBusy={value=>{actionSubmissionRef.current=value;setAddingActions(value);}} onDone={()=>recordAttentionAcknowledgement(selectedSource)}/>}</div>}
            </div>}
            {collaborationRequestId && <p className="text-sm">Yhteistyöpyynnön vastaustila. Tässä keskustelussa ei lueta sähköpostiasi, kalenteriasi tai muistiasi.</p>}
            {emailTaskSuggestionId&&<p className="text-sm">Sähköpostitehtävän käsittely · ei kalenterivarausta.</p>}
            {emailTaskSuggestionId&&(messages.length>1||proposalExpired||failedRequest)&&<button type="button" title="Hylkää keskustelun tallentamattomat muutokset ja lataa alkuperäinen ehdotus" className="min-h-11 text-sm text-primary underline disabled:opacity-40" disabled={thinking||addingActions} onClick={()=>setEmailTaskReload(value=>value+1)}>Palauta alkuperäinen ehdotus</button>}
            {!scoped && <div className="mt-3 flex justify-center gap-2" aria-label="Keskustelun tila">
              {[{value:false,label:"Omat asiat"},{value:true,label:"Yleistieto"}].map(mode => <button key={mode.label} type="button" disabled={thinking || addingActions} aria-pressed={knowledgeMode === mode.value} className={cn("min-h-11 rounded-full border px-4 py-2 text-sm", knowledgeMode === mode.value ? "border-primary text-primary" : "border-border text-muted-foreground")} onClick={() => {
                if (!mode.value && attachmentFiles.length) {setAttachmentError("Poista valitut liitteet ennen siirtymistä omiin asioihin."); return;}
                stopConversation(); setKnowledgeMode(mode.value); setPendingActions([]); setFailedRequest(""); setAttachmentError("");
                proposalRef.current = null; shownProposal.current = null;
              }}>{mode.label}</button>)}
            </div>}
            {knowledgeMode && !scoped && <p className="mt-2 text-xs">Kysy vapaasti tai liitä dokumentti. Henkilökohtaisia tietojasi ei haeta tässä tilassa.</p>}
          </section>
          <section className="voice-hero">
            <VoiceOrb active={conversationActive || captureStatus !== "idle" || thinking || speaking} level={voiceLevel} mode={voiceMode} />
            <div className="voice-state-label" role="status" aria-live="polite">{statusLabel}</div>
            <div className={cn("voice-wave", (listening || playbackActive) && "is-live", voiceMode === "thinking" && "is-thinking", voiceMode === "idle" && "is-idle")}>
              {waveFactors.map((factor, index) => <span key={index} style={{ "--bar-level": Math.max(.12, voiceLevel * factor), animationDelay: `${index * 0.055}s` }} />)}
            </div>
          </section>
          {!scoped && !knowledgeMode && <details className="voice-quick-options"><summary>Esimerkkejä pyynnöistä</summary>
          <section className="voice-quick-grid" aria-label="Pikatoiminnot">
            {QUICK_PROMPTS.map(({ label, prompt, icon: Icon, tone }) => (
              <button type="button" disabled={thinking || speaking || addingActions || listening || captureStatus !== "idle"} key={prompt} onClick={() => { unlockAudio(); submit(prompt); }} className={cn("voice-quick-card", `is-${tone}`)}>
                <Icon /><span>{label}</span><ChevronRight className="quick-chevron" />
              </button>
            ))}
          </section>
          </details>}
          {messages.length > visibleMessageCount && <button type="button" className="min-h-11 text-sm text-primary" onClick={() => setVisibleMessageCount((count) => count + 20)}>Näytä vanhempia viestejä ({messages.length - visibleMessageCount})</button>}
          <section className="voice-conversation" data-testid="voice-transcript" role="log" aria-label="Keskustelu" aria-live={speakerOn ? "off" : "polite"} aria-relevant="additions">
            {messages.length === 0 && <p className="voice-empty-copy">Puhu tai kirjoita viesti. Toimintojen tallentaminen pyytää aina hyväksynnän.</p>}
            {messages.slice(-visibleMessageCount).map((message, index) => <VoiceMessage key={`${message.role}-${messages.length - Math.min(messages.length, visibleMessageCount) + index}`} message={message} />)}
            {knowledgeMode && <span ref={knowledgeEndRef} aria-hidden="true" />}
            {pendingActions.length > 0 && (workContext?<ConversationSolution action={pendingActions[0]} revision={solutionRevision} planning={solutionPlanning} disabled={addingActions||thinking||!pendingActions.every(previewReady)} expired={proposalExpired} onApprove={()=>confirmActions(proposalSignature)} onCancel={cancelActions} onEdit={editPendingRequest} onSpeak={()=>speak(pendingActions.flatMap(previewLines).join(". "))}/>:<div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                <p className="text-sm font-semibold text-foreground">
                  {proposalKind === "day_plan" ? "Päivän suunnitelma — tarkista ennen tallentamista" : "Tarkista ennen tallentamista"}
                </p>
                <ul className="mt-2 space-y-2">
                  {pendingActions.map((action, index) => (
                    <li key={`${action.title}-${index}`} className="rounded-xl border border-border p-3 text-sm text-foreground">
                      <div className="min-w-0 break-words">
                        <b className="block font-medium">{actionTitle(action)}</b>
                        <ActionPreview action={action} />
                      </div>
                      <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                        {actionTypeLabel(action)}
                      </span>
                    </li>
                  ))}
                </ul>
                {proposalExpired && <p className="mt-3 text-xs text-amber-400" role="alert">Ehdotus vanheni turvallisuussyistä. Pyydä assistenttia muodostamaan se uudelleen.</p>}
                {actionError && <p className="mt-3 text-xs text-destructive" role="alert">{actionError}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => confirmActions(proposalSignature)} disabled={addingActions || thinking || proposalExpired || !pendingActions.every(previewReady)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
                    {addingActions ? "Tallennetaan…" : proposalExpired ? "Ehdotus vanheni" : actionError ? "Yritä uudelleen" : pendingActions.some(action => action.type === "collaboration_response") ? "Hyväksy ja jaa vastaus" : pendingActions.some(action => action.type === "collaboration_request") ? "Hyväksy ja jaa sisäinen pyyntö" : "Hyväksy ja tallenna"}
                  </button>
                  <button type="button" onClick={cancelActions} disabled={addingActions} className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground">
                    Peru
                  </button>
                  <button type="button" onClick={editPendingRequest} disabled={addingActions} className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-foreground">
                    Korjaa pyyntöä
                  </button>
                </div>
                <button type="button" disabled={addingActions || thinking} onClick={() => speak(pendingActions.flatMap(previewLines).join(". "))} className="min-h-11 text-sm text-primary">Lue ehdotuksen tiedot ääneen</button>
                <p className="mt-2 text-[11px] text-muted-foreground">Voit hyväksyä tai perua myös puhumalla. Korjaaminen peruu vain avoimen toimintoehdotuksen.</p>
              </div>
            )}
            {thinking && <div className="voice-thinking">ACAI ajattelee…</div>}
            {transcript && <div className="voice-live-transcript">“{transcript}”</div>}
            {!supportsSpeech && <div className="voice-availability-note">Puheentunnistus ei ole käytettävissä tällä laitteella.</div>}
            {voiceNotice && <div className="voice-availability-note" role="status">{voiceNotice}</div>}
            {voiceIssue && (
              <div className="voice-availability-note" role="alert">
                <b>{voiceIssue.stage} epäonnistui.</b> {voiceIssue.message}
              </div>
            )}
          </section>
        </div>
        <div className="voice-input-dock">
          {knowledgeMode && !scoped && <fieldset disabled={thinking || addingActions} className="mb-2 space-y-2 px-1 text-xs">
            <legend className="sr-only">Yleistieto ja liitteet</legend>
            <button type="button" className="assistant-web-toggle" aria-pressed={allowWeb} aria-label={`Verkkohaku ${allowWeb ? "päällä" : "pois päältä"}`} title="Salli tämän kysymyksen tarkistaminen verkosta. Haku tehdään vasta lähettäessäsi viestin." onClick={() => setAllowWeb(current => !current)}><Globe aria-hidden="true" /><span>Verkkohaku</span><span className="assistant-web-state">{allowWeb ? "Päällä" : "Pois"}</span></button>
            {attachmentFiles.map((file,index) => <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2"><span className="min-w-0 break-all">Liite {index+1}: {file.name} ({Math.ceil(file.size/1024)} kt)</span><button type="button" className="min-h-11 shrink-0 px-2 text-primary" aria-label={`Poista liite ${index+1}`} onClick={() => {setAttachmentFiles(current => current.filter((_,i) => i !== index));setAttachmentConsent(false);}}>Poista</button></div>)}
            {attachmentFiles.length > 0 && <label className="flex min-h-11 items-center gap-2"><input type="checkbox" checked={attachmentConsent} onChange={event => {setAttachmentConsent(event.target.checked);setAttachmentError("");}} />Sallin näiden liitteiden käsittelyn Relevance AI:ssa tämän viestin yhteydessä.</label>}
            {attachmentFiles.length > 0 && <p>Kirjoita kysymys ja lähetä liitteet Lähetä-painikkeella. Valitut tiedostot säilyvät vain tässä avoimessa näkymässä.</p>}
            {attachmentError && <p role="alert" className="text-destructive">{attachmentError}</p>}
          </fieldset>}
          {assistantViewport.keyboardOpen && <button type="button" className="voice-dismiss-keyboard" onClick={() => composerRef.current?.blur()}>Sulje näppäimistö</button>}
          {failedRequest && <button type="button" className="min-h-11 text-sm text-primary" onClick={() => {
            if (draftMessage.trim()) { setVoiceNotice("Syöttökentässä on jo luonnos. Säilytä tai tyhjennä se ensin."); return; }
            setDraftMessage(failedRequest); setFailedRequest(""); composerRef.current?.focus();
          }}>Muokkaa epäonnistunutta viestiä</button>}
          <details className="voice-privacy"><summary>{knowledgeMode ? "Tietosuoja ja liitteet" : "Miten puhetta käsitellään?"}</summary><p>{knowledgeMode && "Valinta ei lähetä liitteitä. Voit valita enintään 3 teksti-, kuva- tai PDF-tiedostoa, 2 Mt/tiedosto. Tekstitiedoston on oltava UTF-8-muodossa, enintään 8000 merkkiä. Liian pitkää sisältöä ei katkaista huomaamatta. Lähetä välittää tämän viestin liitteet ja niiden metatiedot suostumuksellasi Relevance AI:lle. ACAI ei tallenna tiedostoja tietokantaan. "}Puhe ja teksti käsitellään palvelimella. Ääntä voidaan lähettää ElevenLabsille puheentunnistukseen, vastauksen tekstiä puhesynteesiin ja keskustelua Relevancelle tulkittavaksi. ACAI ei tallenna raakaääntä tietokantaan. Ulkoisten palveluiden säilytys riippuu niiden asetuksista. Vaihtoehtoisesti voit kirjoittaa ja poistaa vastausäänen käytöstä.</p></details>
          <form className={cn("assistant-message-composer", knowledgeMode && !scoped && "has-attachment-control")} onSubmit={sendTypedMessage}>
            {knowledgeMode && !scoped && <>
              <input ref={attachmentInputRef} type="file" hidden multiple disabled={thinking || addingActions} accept=".txt,text/plain,application/pdf,image/png,image/jpeg,image/webp" aria-label="Valitse teksti-, kuva- tai PDF-tiedostot" onChange={event => {
                const files = [...attachmentFiles,...Array.from(event.target.files || [])];
                const error = validateAssistantFiles(files);
                event.target.value = "";
                if (error) {setAttachmentError(error); return;}
                stopConversation(); setAttachmentFiles(files); setAttachmentConsent(false); setAttachmentError("");
              }} />
              <button type="button" className="assistant-attach-control" disabled={thinking || addingActions || attachmentFiles.length >= 3} aria-label="Liitä teksti, kuva tai PDF" title="Liitä teksti, kuva tai PDF · enintään 3 × 2 Mt" onClick={() => attachmentInputRef.current?.click()}><Paperclip aria-hidden="true" /></button>
            </>}
            <label className="sr-only" htmlFor="assistant-message-input">Kirjoita viesti assistentille</label>
            <textarea
              ref={composerRef}
              id="assistant-message-input"
              data-testid="assistant-message-input"
              value={draftMessage}
              onChange={(event) => setDraftMessage(event.target.value)}
              onFocus={() => {
                if (conversationActiveRef.current || listening || speaking) stopConversation();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              rows={1}
              maxLength={6000}
              placeholder={workContext&&pendingActions.length?"Kerro haluamasi muutos…":"Kirjoita viesti…"}
              aria-label="Kirjoita viesti assistentille"
            />
            <button
              type="submit"
              data-testid="assistant-message-send"
              className="assistant-message-send"
              disabled={!draftMessage.trim() || thinking || addingActions || (workContext&&(!selectedSource||sourceError)) || captureStatus === "transcribing" || (attachmentFiles.length > 0 && !attachmentConsent)}
              aria-label="Lähetä viesti"
            >
              <Send />
            </button>
          </form>
          <section className="voice-controls" aria-label="Puheohjaimet">
            <button type="button" onClick={toggleSpeaker} className={cn("voice-side-control", speakerOn && "is-on")} aria-label={speakerOn ? "Poista ääni käytöstä" : "Ota ääni käyttöön"} aria-pressed={speakerOn}><span><Speaker /></span><b>{speakerOn ? "Ääni päällä" : "Ääni pois"}</b></button>
            <div className="voice-mic-stack">
              <button
                type="button"
                data-testid="hold-to-talk-btn"
                onClick={startConversation}
                disabled={primaryControlDisabled}
                className={cn("voice-hold-control", conversationActive && "is-listening")}
                aria-label={primaryControlLabel}
                aria-pressed={listening}
              ><Mic /></button>
              <b>{primaryControlLabel}</b>
            </div>
            <button type="button" onClick={stopConversation} disabled={!conversationActive} className={cn("voice-side-control", conversationActive && "is-on")} aria-label="Lopeta jatkuva kuuntelu"><span><Square className="stop-square" /></span><b>Lopeta kuuntelu</b></button>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
